import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Sidebar, MainContent, WhatsAppButton, Footer } from './layout';
import { useAuth } from '../contexts/AuthContext';
import { useUI } from '../contexts/UIContext';
import { SkipLink } from '../../../shared/components';
import { useToast } from '../../../shared/contexts/ToastContext';

interface MemberAreaLayoutProps {
  children: React.ReactNode;
}

const MemberAreaLayout: React.FC<MemberAreaLayoutProps> = ({ children }) => {
  const { user, logout, isLoading } = useAuth();
  const { sidebarOpen, toggleSidebar, closeSidebar } = useUI();
  const navigate = useNavigate();
  const { showInfo } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      
      // Show logout success notification
      showInfo('Anda telah keluar. Sampai jumpa lagi! 👋');
      
      // Small delay to ensure toast is visible
      await new Promise(resolve => setTimeout(resolve, 500));
      
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if logout fails
      navigate('/login', { replace: true });
    }
  };

  // Helper function to check if user data is complete
  const isUserDataComplete = (userData: typeof user) => {
    if (!userData) return false;
    // Check if user has real data (not just default values from token decode)
    return userData.fullName && userData.fullName !== 'user' && userData.fullName !== userData.email?.split('@')[0];
  };

  // Show loading state while checking authentication or if user data is incomplete
  // This prevents showing default values that look like "new user"
  if (isLoading || (user && !isUserDataComplete(user))) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600">Memuat data akun...</p>
        </div>
      </div>
    );
  }

  // Create guest user object if no user is authenticated
  const displayUser = user || {
    username: 'Guest',
    balance: 0,
    role: 'guest' as const
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to main content link for keyboard navigation */}
      <SkipLink />
      
      <Header
        user={{
          username: displayUser.username,
          role: displayUser.role,
          avatar: user?.avatar,
          balance: displayUser.balance
        }}
        onLogout={handleLogout}
        onMenuClick={toggleSidebar}
        sidebarOpen={sidebarOpen}
      />
      
      <Sidebar
        user={{
          username: displayUser.username,
          balance: displayUser.balance,
          role: displayUser.role,
          stats: undefined // Stats will be loaded separately if needed
        }}
        isOpen={sidebarOpen}
        onClose={closeSidebar}
      />
      
      <MainContent>{children}</MainContent>
      
      <Footer />
      
      <WhatsAppButton phoneNumber="6289669654782" />
    </div>
  );
};

export default MemberAreaLayout;
